import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import {
  libraryErrorField,
  useAlertDestinationRegistryQuery,
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryAssetsQuery,
  type LibraryAsset,
} from "@/api/library";
import {
  attributeMatrixFailure,
  emptyMatrixDraft,
  findMatrixWarnings,
  isAlertMatrixContent,
  toAlertMatrixContent,
  toMatrixDraft,
  type DraftAlertMatrix,
  type MatrixProblem,
} from "@/lib/alertMatrix";
import { sectionForAssetType } from "@/lib/librarySections";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select } from "@/ui/Input";
import { ErrorState, LoadingState } from "@/ui/states";
import { FieldAnchorProvider } from "@/ui/validation/FieldAnchorContext";

import { AlertMatrixEditor } from "./AlertMatrixEditor";
import { createFieldErrors } from "./NewAssetDialog";

/**
 * Create an alert matrix at v1, from scratch or as a copy of an existing one
 * (#494).
 *
 * WHY THIS EXISTS RATHER THAN AN ENTRY IN `NewAssetDialog`. That dialog authors
 * the ORDERED-PARTS envelope — a list of pack-file documents — and a matrix is
 * typed content: background domains, their flag thresholds, cross-domain
 * escalations, destination references and per-severity routing.
 * `CREATABLE_ASSET_TYPES` excludes it for exactly that reason, and adding it
 * there would have produced a create form that cannot express what it creates.
 * This is #368's seam, opened for the second family of typed content.
 *
 * WITHOUT IT THE MATRIX EDITOR HAS NOTHING TO EDIT. The only matrix in
 * existence is the seeded `Default alert matrix`, which is base canon and
 * refuses content writes; the service's own refusal says "copy it into a new
 * asset or override per project", and until now that instruction had no
 * destination for this family. That is the `onCopy={null}` dead end #35 left
 * behind and named as a follow-up in the route itself.
 *
 * A COPY IS AN ORDINARY CREATE, as in #33 and #368: there is no server-side copy
 * route and none is needed. The copy is this request carrying content read from
 * the source's head version, which keeps the new matrix genuinely independent —
 * `seeded: false`, `lockedFloor: false`, its own version chain starting at v1 —
 * rather than a reference back to canon that would inherit canon's refusals. The
 * seeded matrix it was copied from stays exactly as read-only as it was.
 *
 * NO `placementCoordinate` IS SENT, and that is not an omission. `alert_matrix`
 * is not in `PLACEABLE_ASSET_TYPES` (`editor_service/placement.py`), and the
 * create schema refuses a coordinate for every type outside it — so a control
 * for one would invite a refusal the clinician cannot act on. This is the one
 * way this dialog differs from `NewAssetDialog`, which collects a coordinate for
 * the types that require one.
 *
 * The starting content is edited HERE rather than after creation because v1 is a
 * version like any other: it is immutable the moment it exists, and a matrix's
 * coverage invariants are enforced on it. Creating first and fixing after would
 * leave a permanent, undeletable v1 whose alerts route nowhere.
 */

const BLANK_SOURCE = "";
const NAME_MAX_LENGTH = 200;
const NOTE_MAX_LENGTH = 500;

export function NewAlertMatrixDialog({
  onClose,
  copyFrom,
}: {
  onClose: () => void;
  /** Opens with this matrix preselected as the content source. */
  copyFrom?: LibraryAsset;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const catalog = useLibraryAssetsQuery(false);
  const destinationRegistry = useAlertDestinationRegistryQuery();
  const destinationAuthoringAvailable =
    destinationRegistry.isSuccess &&
    destinationRegistry.data.entries.some((entry) => entry.lifecycle === "active");
  const nameErrorId = useId();

  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [note, setNote] = useState(
    copyFrom
      ? `Copied from "${copyFrom.name}" at ${copyFrom.latestVersion}.`
      : "",
  );
  const [sourceId, setSourceId] = useState(copyFrom?.id ?? BLANK_SOURCE);
  const [draft, setDraft] = useState<DraftAlertMatrix>(emptyMatrixDraft);
  /** The source whose content `draft` currently reflects. */
  const [seededFrom, setSeededFrom] = useState(BLANK_SOURCE);
  const [clientProblems, setClientProblems] = useState<MatrixProblem[]>([]);

  const assets = catalog.data?.items ?? [];
  // The active catalog deliberately excludes archived assets. A direct copy,
  // however, already carries the exact source row the clinician chose, and an
  // archived asset remains readable and copyable even though it is no longer an
  // option in the active roster.
  const source =
    (copyFrom?.id === sourceId ? copyFrom : null) ??
    assets.find((asset) => asset.id === sourceId) ??
    null;
  const sourceContent = useAssetVersionQuery(
    source?.id ?? "",
    source ? source.latestVersion : null,
  );

  if (
    source === null &&
    destinationRegistry.isSuccess &&
    draft.destinations.length === 1 &&
    draft.destinations[0]?.ref === ""
  ) {
    const first = destinationRegistry.data.entries.find(
      (entry) => entry.lifecycle === "active",
    );
    if (first) {
      setDraft({
        ...draft,
        destinations: [{ ...draft.destinations[0], ref: first.ref }],
      });
    }
  }

  const sourceMapping =
    source &&
    seededFrom !== source.id &&
    sourceContent.isSuccess &&
    sourceContent.data.assetId === source.id &&
    isAlertMatrixContent(sourceContent.data.content)
      ? toMatrixDraft(sourceContent.data.content)
      : null;

  // Same render-time derivation as the editors: the starting matrix IS the
  // source's content, so an effect would flash a blank surface over content
  // already in hand. Guarded on the source id, so a clinician's subsequent
  // edits are never re-seeded away.
  if (
    source &&
    seededFrom !== source.id &&
    sourceContent.isSuccess &&
    sourceContent.data.assetId === source.id &&
    isAlertMatrixContent(sourceContent.data.content) &&
    sourceMapping?.ok
  ) {
    setDraft(sourceMapping.draft);
    setSeededFrom(source.id);
    setClientProblems([]);
  }

  const copyCandidates = [
    ...(copyFrom?.assetType === "alert_matrix" ? [copyFrom] : []),
    ...assets.filter(
      (asset) => asset.assetType === "alert_matrix" && asset.id !== copyFrom?.id,
    ),
  ];

  // The SAME derivation #33's dialog uses, not a second copy of it: which
  // control a refused create belongs to is a property of the route's error
  // envelope, and this dialog posts to that same route.
  const nameError = create.isError
    ? (createFieldErrors(create.error).name ?? null)
    : null;
  const contentMessage =
    create.isError &&
    create.error instanceof ApiError &&
    libraryErrorField(create.error)?.startsWith("content")
      ? create.error.message
      : null;
  const generalMessage =
    create.isError && !contentMessage && nameError === null
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null;

  const serverProblems: MatrixProblem[] = contentMessage
    ? [
        {
          path: attributeMatrixFailure(contentMessage) ?? "content",
          message: contentMessage,
        },
      ]
    : [];

  const busy = create.isPending;
  const nameMissing = name.trim().length === 0;
  // A copy whose source content has not arrived would be created from the blank
  // matrix instead — an asset silently missing everything it was meant to copy.
  // Fails closed until the content is in hand.
  const awaitingSource = source !== null && seededFrom !== source.id;
  // …but "not in hand" is not the same fact as "still coming". The seeded-copy
  // journey is ticket-required, and a source read that has definitively STOPPED
  // — a 5xx, or a payload that is not a matrix — would otherwise leave this
  // dialog rendering "Loading content…" forever with Create disabled and no way
  // out. That is the defect `42a25944` fixed for questionnaires, and it is the
  // same shape here: each terminal state is distinguished from pending, so the
  // clinician sees the reason and, where retrying can help, a way to retry.
  const sourceUnreadable = awaitingSource && sourceContent.isError;
  const sourceIsNotMatrix =
    awaitingSource &&
    sourceContent.isSuccess &&
    sourceContent.data.assetId === source.id &&
    !isAlertMatrixContent(sourceContent.data.content);
  const sourceMappingFailed = sourceMapping?.ok === false;
  const sourceLoading =
    awaitingSource &&
    !sourceUnreadable &&
    !sourceIsNotMatrix &&
    !sourceMappingFailed;
  const sourceErrorMessage =
    sourceContent.error instanceof ApiError
      ? sourceContent.error.message
      : "The Editor Service could not be reached.";

  function submit() {
    const result = toAlertMatrixContent(draft);
    if (!result.ok) {
      setClientProblems(result.problems);
      return;
    }
    setClientProblems([]);
    create.mutate(
      {
        assetType: "alert_matrix",
        name: name.trim(),
        content: result.content,
        note: note.trim() === "" ? null : note.trim(),
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(
            `/library/${sectionForAssetType(created.assetType)}/${created.id}`,
          );
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="w-[min(56rem,calc(100vw-2rem))]"
        title={copyFrom ? `Copy "${copyFrom.name}"` : "New alert matrix"}
        description="Creates a clinician-owned alert matrix at v1. It is editable from the moment it exists, carries none of a seeded matrix's read-only state, and every project must pin it explicitly before it changes anything."
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                nameMissing ||
                awaitingSource ||
                !destinationAuthoringAvailable
              }
              onClick={submit}
            >
              Create alert matrix
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name">
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  aria-invalid={nameError !== null || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    nameError ? nameErrorId : undefined,
                  )}
                  onChange={(event) => setName(event.target.value)}
                />
                {nameError ? (
                  <FieldError id={nameErrorId} message={nameError} />
                ) : null}
              </>
            )}
          </Field>

          <Field
            label="Starting content"
            description="Copying reads the source's current version. The new matrix is independent from then on — it carries none of the source's seeded or locked state."
          >
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                value={sourceId}
                disabled={copyFrom !== undefined}
                aria-describedby={describedBy}
                onChange={(event) => {
                  const next = event.target.value;
                  setSourceId(next);
                  if (next === BLANK_SOURCE) {
                    setSeededFrom(BLANK_SOURCE);
                    setDraft(emptyMatrixDraft());
                  }
                  setClientProblems([]);
                  create.reset();
                }}
              >
                <option value={BLANK_SOURCE}>Start from a blank matrix</option>
                {copyCandidates.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    Copy of {asset.name} ({asset.latestVersion})
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {sourceLoading ? (
            <LoadingState
              label={`Loading content from ${source?.name ?? "the source matrix"}`}
              rows={3}
            />
          ) : sourceUnreadable ? (
            <ErrorState
              title={`${source?.name ?? "The source matrix"} could not be read`}
              description={`${sourceErrorMessage} Nothing has been created, so retrying is safe.`}
              onRetry={() => {
                void sourceContent.refetch();
              }}
            />
          ) : sourceIsNotMatrix ? (
            // Fails closed WITHOUT a retry: the same payload will come back, so
            // offering one would only repeat itself. Create stays disabled.
            <ErrorState
              title={`${source?.name ?? "That asset"} is not an alert matrix`}
              description="Its content cannot start an alert matrix, so it cannot be copied here. Choose another source, or start from a blank matrix."
            />
          ) : sourceMappingFailed ? (
            <ErrorState
              title={`${source?.name ?? "The source matrix"} cannot be copied safely`}
              description={`${sourceMapping.problem.message} Nothing has been created.`}
            />
          ) : (
            <FieldAnchorProvider>
              <AlertMatrixEditor
                draft={draft}
                onChange={(next) => {
                  setDraft(next);
                  setClientProblems([]);
                  create.reset();
                }}
                problems={[...clientProblems, ...serverProblems]}
                warnings={findMatrixWarnings(draft)}
                destinationRegistry={destinationRegistry.data ?? null}
                destinationRegistryState={
                  destinationRegistry.isPending
                    ? "pending"
                    : destinationRegistry.isSuccess
                      ? "ready"
                      : "error"
                }
                onRetryDestinationRegistry={() => {
                  void destinationRegistry.refetch();
                }}
              />
            </FieldAnchorProvider>
          )}

          <Field
            label="Version note"
            description="Recorded against v1 and shown in the version history."
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                value={note}
                maxLength={NOTE_MAX_LENGTH}
                aria-describedby={describedBy}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>

          {generalMessage ? (
            <p role="alert" className="text-sm text-destructive">
              {generalMessage}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
